import dayjs from 'dayjs';
import type { MilestoneStatus } from '../types/milestones';

/** Days after the due date before Past Due turns from yellow (grace) to red. */
export const MILESTONE_PAST_DUE_GRACE_DAYS = 45;

export type MilestonePastDueBadgeStatus = 'past_due_grace' | 'past_due';

/**
 * Calendar days past the due date, or null when not overdue / completed.
 */
export function getMilestoneDaysPastDue(
  status: MilestoneStatus,
  dueDate: string,
  now: dayjs.ConfigType = dayjs()
): number | null {
  if (status === 'completed') {
    return null;
  }

  const today = dayjs(now).startOf('day');
  const due = dayjs(dueDate).startOf('day');
  if (!due.isValid()) {
    return null;
  }

  const daysPastDue = today.diff(due, 'day');
  return daysPastDue > 0 ? daysPastDue : null;
}

/** Label like "12d past due". */
export function formatMilestonePastDueLabel(daysPastDue: number): string {
  return `${daysPastDue}d past due`;
}

/**
 * Returns the Past Due badge status for a milestone, or null if it is not past due.
 * Within the grace period the badge is yellow; at/after grace days it is red.
 */
export function getMilestonePastDueBadgeStatus(
  status: MilestoneStatus,
  dueDate: string,
  now: dayjs.ConfigType = dayjs()
): MilestonePastDueBadgeStatus | null {
  const daysPastDue = getMilestoneDaysPastDue(status, dueDate, now);
  if (daysPastDue == null) {
    return null;
  }

  if (daysPastDue < MILESTONE_PAST_DUE_GRACE_DAYS) {
    return 'past_due_grace';
  }

  return 'past_due';
}

/** Accent color for a subtle left strip on past-due milestone cards. */
export function getMilestonePastDueAccent(
  status: MilestoneStatus,
  dueDate: string,
  now: dayjs.ConfigType = dayjs()
): 'yellow' | 'red' | null {
  const pastDueStatus = getMilestonePastDueBadgeStatus(status, dueDate, now);
  if (pastDueStatus === 'past_due_grace') return 'yellow';
  if (pastDueStatus === 'past_due') return 'red';
  return null;
}
